import { API_URL } from "./Config";
import { formatDate } from "./DateUtils";
import { FetchResponse } from "./FetchResponse";

import {
  GetComplianceListByProfileIdAsync,
  GetComplianceListDummy,
  IComplianceList,
} from "./ICompliance";

import {
  GetSiteDetailsByProfileIdAsync,
  GetSiteDetailsDummy,
  ISiteDetails,
} from "./ISiteDetails";
import {
  GetActiveLicenceVersionDummy,
  GetAllLicenceVersionsAsync,
  GetLicenceVersionAsync,
  GetLicenceVersionsDummy,
  ILicenceVersion,
  ILicenceVersionsList,
} from "./LicenceVersion";

export interface ILicenceProfile {
  id: string;
  profileNo: string;
  profileName: string;
  version: string;
  dateIssued: string;
  type: string;
  status: string;
  subStatus: string;
  sector: string;
  majorActivity: string;
  iedActivity: string;
  organisation: string;
  firstIssued: string;
  county: string;
  prioritySite: string;

  siteDetails: ISiteDetails;
  complianceList: IComplianceList;
  licenceVersionsList: ILicenceVersionsList;
  activeLicenceVersion: ILicenceVersion;
}

interface ILicenceProfileApiResponse {
  licence_profile_id: string;
  name: string;
  profile_number: string;
  active_licence_id: string;
  active_licence_type: string;
  active_licence_regno: string;
  active_licence_startdate: string;
  active_licence_status: string;
  active_licence_substatus: string;
  active_licence_sector: string;
  first_issue_date: string;
  county: string;
  town: string;
  organisation_id: string;
  organisation_name: string;
  uww_priority_site: string;
  major_class_of_activity: string;
  ied_activity: string;
  site_id: string;
}

export interface ILicenceProfileResponse {
  response: FetchResponse;
  licenceProfile?: ILicenceProfile;
}

export async function GetLicenceProfileAsync(
  profileNo: string
): Promise<ILicenceProfileResponse> {
  var url = `${API_URL}/LicenceProfile/byprofileno?licence_profile_no=${profileNo}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    if (profileNo === "XXXX") {
      url = `${API_URL}/notfound`;
    } else {
      url = `${API_URL}/licenceprofile`;
    }
  }

  var response = await fetch(url);
  if (!response.ok) {
    return {
      response: {
        ok: false,
        httpStatusCode: response.status,
        httpErr: response.statusText,
      },
    };
  }

  var body = <ILicenceProfileApiResponse>await response.json();

  var profileId = body.licence_profile_id;

  var siteDetails = await GetSiteDetailsByProfileIdAsync(profileId);
  var complianceList = await GetComplianceListByProfileIdAsync(profileId, {
    page: 1,
    per_page: 5,
  });

  var versions = await GetAllLicenceVersionsAsync(profileId);
  var activeVersion = await GetLicenceVersionAsync(body.active_licence_id);

  const profile = <ILicenceProfile>{
    county: body.county,
    dateIssued: formatDate(body.active_licence_startdate),
    firstIssued: formatDate(body.first_issue_date),
    id: body.licence_profile_id,
    iedActivity: body.ied_activity,
    majorActivity: body.major_class_of_activity,
    organisation: body.organisation_name,
    profileName: body.name,
    profileNo: body.profile_number,
    sector: body.active_licence_sector,
    status: body.active_licence_status,
    subStatus: body.active_licence_substatus,
    type: body.active_licence_type,
    version: body.active_licence_regno,
    prioritySite: body.uww_priority_site,
    siteDetails: siteDetails,
    complianceList: complianceList,
    licenceVersionsList: versions,
    activeLicenceVersion: activeVersion,
  };

  return <ILicenceProfileResponse>{
    response: {},
    licenceProfile: profile,
  };
}

export const ParseGetLicenceProfileResponse = (response: any) => {
  return response as ILicenceProfile;
};

export function GetLicenceProfile(profileNo: string): ILicenceProfile {
  return GetLicenceProfileDummy(profileNo);
}

export const GetLicenceProfileDummy = (profileNo: string): ILicenceProfile => {
  return {
    id: "{guid}",
    profileNo: profileNo,
    profileName: "XYZ Company",
    version: "P9999-04",
    dateIssued: "12 Mar 2019",
    type: "IEL",
    status: "Licensed",
    subStatus: "Licensed Activity Commenced",
    sector: "Surface Coating Sector",
    majorActivity: "12.2.1 - Surface Coatings",
    iedActivity: "6.7 - Other Activities",
    organisation: "XYZ Company",
    firstIssued: "01 Jan 2015",
    county: "Sligo",
    prioritySite: "Priority Site",
    siteDetails: GetSiteDetailsDummy(""),
    complianceList: GetComplianceListDummy(""),
    licenceVersionsList: GetLicenceVersionsDummy(""),
    activeLicenceVersion: GetActiveLicenceVersionDummy(""),
  };
};

export const isRadiologicalProtection = (licenceProfile: ILicenceProfile) => {
  return licenceProfile.type === "Radiological Protection";
};
